import { useCallback, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  Container,
  Flex,
  IconButton,
  ScrollArea,
  Text,
  Skeleton,
} from "@radix-ui/themes";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import {
  CreateSampleConfigurationsParams,
  CreateSampleFormParams,
  useSamplingApi,
} from "@/v2/entities/sampling/Sampling.api";
import { SampleForm } from "@/v2/features/sampling/SampleForm";
import { SampleFormSkeleton } from "@/v2/features/sampling/SampleFormSkeleton";

function CreateSamplePage() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const navigate = useNavigate();
  const samplingApi = useSamplingApi();

  const {
    data: sampleForm,
    isLoading: isLoadingSampleForm,
    isError: isErrorSampleForm,
  } = useQuery({
    queryKey: ["sample-form", id],
    queryFn: () => samplingApi.getSampleForm(Number(id)),
    enabled: id != null,
    onError: () => {
      toast.error("Scorecard not found");
    },
  });

  const { mutateAsync: updateSampleForm } = useMutation(
    samplingApi.updateSampleForm
  );

  const { mutateAsync: createSampleConfigurations } = useMutation(
    samplingApi.createSampleConfigurations
  );

  const { mutateAsync: updateSampleConfiguration } = useMutation(
    samplingApi.updateSampleConfiguration
  );

  const [submitting, setSubmitting] = useState(false);

  const initialValues = useMemo<
    (CreateSampleFormParams & CreateSampleConfigurationsParams) | undefined
  >(() => {
    if (sampleForm == null) {
      return;
    }

    return {
      ...sampleForm,
      questions:
        sampleForm.questions?.map((question) => ({
          key: question.key,
          title: question.title,
          content: question.content,
          type: question.type,
          brief: question.brief ?? undefined,
        })) ?? [],
      configurations:
        sampleForm.sampleConfiguration?.map((configuration) => ({
          id: configuration.id,
          numberOfTickets: configuration.numberOfTickets ?? undefined,
          daysUntilNextRun: configuration.daysUntilNextRun,
          isEnabled: configuration.isEnabled,
          qaRules: configuration.qaRules,
          sampleFormId: configuration.sampleFormId ?? undefined,
          sources: configuration.sources ?? ["tickets"],
        })) ?? [],
    };
  }, [sampleForm]);

  const update = useCallback(
    async ({
      configurations,
      ...data
    }: CreateSampleFormParams & CreateSampleConfigurationsParams) => {
      if (sampleForm == null) {
        return;
      }

      const res = await updateSampleForm({
        id: sampleForm.id,
        ...data,
      });

      const existingConfigurationIds = new Set(
        sampleForm.sampleConfiguration?.map((configuration) => configuration.id)
      );

      const existingConfigurations = configurations.filter(
        (configuration) =>
          configuration.id != null &&
          existingConfigurationIds.has(configuration.id)
      );

      const newConfigurations = configurations.filter(
        (configuration) =>
          configuration.id == null ||
          !existingConfigurationIds.has(configuration.id)
      );

      const promises = [
        ...(newConfigurations.length > 0
          ? [
              createSampleConfigurations({
                configurations: newConfigurations.map(
                  ({ id, ...configuration }) => ({
                    ...configuration,
                    sampleFormId: res.id,
                  })
                ),
              }),
            ]
          : []),
        ...(existingConfigurations.length > 0
          ? existingConfigurations.map((configuration) =>
              updateSampleConfiguration({
                ...configuration,
                sampleFormId: res.id,
              })
            )
          : []),
      ];

      await Promise.all(promises);

      return res;
    },
    [updateSampleForm, sampleForm]
  );

  const handleSubmit = useCallback(
    async (data: CreateSampleFormParams & CreateSampleConfigurationsParams) => {
      const name = data.name;

      setSubmitting(true);
      toast.promise(update(data), {
        loading: "Updating scorecard...",
        success: () => {
          setSubmitting(false);
          navigate("/quality/scorecards");
          queryClient.invalidateQueries(["sample-form", id]);

          return {
            message: `${name} updated successfully`,
          };
        },
        error: "Failed to update scorecard",
      });
    },
    [update, navigate, queryClient, id]
  );

  if (isLoadingSampleForm) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 2000,
          background: "var(--backgroundDefault)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Flex
          justify="start"
          align="center"
          py={"4"}
          px={"8"}
          gap={"4"}
          style={{
            borderBottom: "1px solid var(--gray-4)",
            boxShadow: "var(--shadow-1)",
          }}
        >
          <IconButton
            variant="ghost"
            color="gray"
            onClick={() => navigate("/quality/scorecards")}
          >
            <ArrowLeftIcon />
          </IconButton>

          <Skeleton className="w-[100px] h-[20px]" />
        </Flex>

        <ScrollArea style={{ flexGrow: 1 }}>
          <Container size="4" style={{ paddingBlock: 32 }}>
            <SampleFormSkeleton />
          </Container>
        </ScrollArea>
      </div>
    );
  }

  if (isErrorSampleForm) {
    return <Navigate to="/quality/scorecards" replace />;
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 2000,
        background: "var(--backgroundDefault)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Flex
        justify="start"
        align="center"
        py={"4"}
        px={"8"}
        gap={"4"}
        style={{
          borderBottom: "1px solid var(--gray-4)",
          boxShadow: "var(--shadow-1)",
        }}
      >
        <IconButton variant="ghost" color="gray" onClick={() => navigate(-1)}>
          <ArrowLeftIcon />
        </IconButton>

        <Text size="2" color="gray" weight="medium" highContrast>
          Edit Scorecard
        </Text>
      </Flex>

      <ScrollArea style={{ flexGrow: 1 }}>
        <Container size="4" style={{ paddingBlock: 32 }}>
          <SampleForm
            mode="update"
            initialValues={initialValues}
            onSubmit={handleSubmit}
            loading={submitting}
            submitLabel="Update Scorecard"
          />
        </Container>
      </ScrollArea>
    </div>
  );
}

export default CreateSamplePage;
